/**
 * usePushNotifications
 *
 * Handles the full FCM web-push lifecycle for an authenticated user:
 *  1. On login — if permission is already "granted", silently registers the
 *     FCM token with the backend (no browser prompt needed).
 *  2. requestPushPermission() — called from a UI button so the browser
 *     permission prompt is triggered by a real user gesture (required by
 *     Chrome 94+). On grant, obtains the FCM token and POSTs it to
 *     POST /users/me/push-tokens.
 *  3. Listens for FOREGROUND messages and shows a toast notification.
 *  4. On logout — DELETE /users/me/push-tokens/{id} soft-revokes the token.
 */
import { useEffect, useState } from 'react'
import { getToken, deleteToken, onMessage } from 'firebase/messaging'
import { messaging } from '@/lib/firebase'
import { useAuthStore } from '@/store/auth'
import { toast, playNotificationSound } from '@/store/toast'
import { registerPushToken, revokePushToken } from '@/api/users'

const VAPID_KEY    = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined
const SW_PATH      = '/firebase-messaging-sw.js'
const TOKEN_KEY    = 'fcm_token'
const TOKEN_ID_KEY = 'fcm_token_id'

/** Wait for a service worker to become active, with a timeout fallback. */
function waitForSWActive(registration: ServiceWorkerRegistration, timeoutMs = 6000): Promise<void> {
  return new Promise<void>((resolve) => {
    if (registration.active) { resolve(); return }
    const sw = registration.installing ?? registration.waiting
    if (!sw) { resolve(); return }

    const timer = setTimeout(resolve, timeoutMs) // never hang forever

    const handler = () => {
      if (sw.state === 'activated' || sw.state === 'redundant') {
        clearTimeout(timer)
        sw.removeEventListener('statechange', handler)
        resolve()
      }
    }
    sw.addEventListener('statechange', handler)
  })
}

export async function acquireAndStoreToken(): Promise<void> {
  if (!messaging || !VAPID_KEY) {
    console.warn('[FCM] Messaging not available or VAPID key missing')
    return
  }
  if (!('serviceWorker' in navigator)) return

  // Always register fresh — updateViaCache: 'none' ensures the browser checks
  // for a new SW version on every call instead of serving a cached copy.
  const registration = await navigator.serviceWorker.register(SW_PATH, {
    updateViaCache: 'none',
  })

  // The SW ships skipWaiting() so it activates without waiting for other tabs,
  // but we still wait up to 6 s for safety before calling getToken.
  await waitForSWActive(registration)

  let token: string
  try {
    token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    })
  } catch (err) {
    // PATCH CORS error: stale token in IndexedDB triggers a PATCH refresh which
    // Google's CORS policy blocks. Delete the old token and retry with fresh POST.
    console.warn('[FCM] getToken failed (likely stale token CORS issue), retrying fresh:', err)
    try {
      await deleteToken(messaging)
    } catch {
      // ignore deleteToken errors
    }
    token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    })
  }
  if (!token) {
    console.warn('[FCM] getToken returned empty — check VAPID key and SW registration')
    return
  }

  const storedToken = localStorage.getItem(TOKEN_KEY)
  if (token !== storedToken) {
    localStorage.setItem(TOKEN_KEY, token)
    try {
      const record = await registerPushToken({
        token,
        platform: 'web',
        device_name: navigator.userAgent.slice(0, 100),
      })
      localStorage.setItem(TOKEN_ID_KEY, record.id)
    } catch (err) {
      // Token saved locally — retry registration next time
      console.warn('[FCM] Token registration with backend failed:', err)
    }
  }
}

/**
 * Call this from a button click so the browser permission prompt is
 * triggered by a real user gesture.
 * Returns the resulting Notification.permission value.
 */
export async function requestPushPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied'
  try {
    const permission = await Notification.requestPermission()
    if (permission === 'granted') await acquireAndStoreToken()
    return permission
  } catch (err) {
    console.warn('[FCM] Permission request failed:', err)
    return 'denied'
  }
}

export function usePushNotifications() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  // ── Play custom sound when SW fires background push ────────────────────────
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'PLAY_NOTIFICATION_SOUND') playNotificationSound()
    }
    navigator.serviceWorker.addEventListener('message', handler)
    return () => navigator.serviceWorker.removeEventListener('message', handler)
  }, [])

  // ── Auto-register on login when permission already granted ────────────────
  useEffect(() => {
    if (!isAuthenticated || !messaging || !VAPID_KEY) return
    if (!('Notification' in window) || Notification.permission !== 'granted') return

    let unsub: (() => void) | undefined

    async function init() {
      try {
        await acquireAndStoreToken()

        // Foreground message handler — show a toast since the SW won't fire
        // showNotification when the app tab is active/focused.
        unsub = onMessage(messaging!, (payload) => {
          const title   = payload.notification?.title ?? payload.data?.title ?? 'Notification'
          const body    = payload.notification?.body  ?? payload.data?.body  ?? ''
          const refType = payload.data?.ref_type
          const refId   = payload.data?.ref_id

          if (refType === 'report_result' && refId) {
            // Report-ready notification — navigate to the report result page
            const text = body ? `${title}: ${body}` : title
            toast.info(text)
            window.location.assign(`/reports?result=${encodeURIComponent(refId)}`)
          } else {
            toast.info(body ? `${title}: ${body}` : title)
          }
        })
      } catch (err) {
        console.warn('[FCM] Push setup failed:', err)
      }
    }

    void init()
    return () => { unsub?.() }
  }, [isAuthenticated])

  // ── Unregister on logout ───────────────────────────────────────────────────
  useEffect(() => {
    if (isAuthenticated) return
    const id = localStorage.getItem(TOKEN_ID_KEY)
    if (!id) return

    revokePushToken(id)
      .catch(() => { /* ignore — token will expire naturally */ })
      .finally(() => {
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(TOKEN_ID_KEY)
      })
  }, [isAuthenticated])
}

/** Returns live notification permission state (re-renders when it changes). */
export function useNotificationPermission(): NotificationPermission {
  const [perm, setPerm] = useState<NotificationPermission>(() =>
    'Notification' in window ? Notification.permission : 'denied'
  )

  useEffect(() => {
    if (!('permissions' in navigator)) return
    let cancelled = false
    navigator.permissions.query({ name: 'notifications' }).then((status) => {
      if (cancelled) return
      const update = () => setPerm(status.state as NotificationPermission)
      status.addEventListener('change', update)
      return () => status.removeEventListener('change', update)
    }).catch(() => { /* not all browsers support querying */ })
    return () => { cancelled = true }
  }, [])

  return perm
}
